import classnames from 'classnames';
import { Field, FieldProps, Form, useFormikContext } from 'formik';
import { useCallback, useState } from 'react';
import { Button, Flex, List } from 'yoso';
import { Checkbox } from '~/components/Form/Checkbox';
import { required } from '~/components/Form/Form.hooks';
import { T } from '~/components/I18n';
import { Chevron } from '~/components/Icon/Chevron';
import { useMember } from '~/components/Member/Member.hooks';
import { TermLink } from '~/components/Term/Link/TermLink';
import styles from './CookieConsentForm.module.scss';

export const CookieConsentForm = () => {
  const { setValues, submitForm, isValid, isSubmitting } = useFormikContext();
  const [isSettingsVisible, setIsSettingsVisible] = useState(false);
  const { data } = useMember();
  const { region = 'GL' } = data || {};

  const onSettingsButtonClick = useCallback(() => {
    setIsSettingsVisible((visible) => !visible);
  }, []);
  const onAcceptAllButtonClick = useCallback(() => {
    setValues({ req: true, ana: true, mkt: true });
    return submitForm();
  }, [setValues, submitForm]);

  const disabled = !isValid || isSubmitting;

  return (
    <Form className={styles.root}>
      <div className={styles.content}>
        <div className={styles.header}>
          <legend className={styles.description}>
            <T
              name={
                region === 'KR'
                  ? 'citizen_cookieconsent_desc_kr'
                  : 'citizen_cookieconsent_desc_global'
              }
            />{' '}
            <TermLink href={`${import.meta.env.VITE_DOSI_MEMBERS_URL}/terms/privacy_policy/pp`}>
              <T name="citizen_cookie_desc_learnmore_2" />
            </TermLink>
          </legend>
        </div>
        <div className={styles.body}>
          <div>
            <Button
              className={classnames(styles.more, { [styles.open]: isSettingsVisible })}
              variant="text"
              onClick={onSettingsButtonClick}>
              <T name="citizen_cookie_desc_cookiesettings" />
              <Chevron />
            </Button>
          </div>

          {isSettingsVisible && (
            <List className={styles.list}>
              <li>
                <Field name="req" type="checkbox" validate={required}>
                  {({ field }: FieldProps) => (
                    <Flex container as="label">
                      <Checkbox className={styles.checkbox} {...field} variant="circle" disabled />
                      <Flex className={styles.labelText} flex={1}>
                        <strong>
                          <T name="dosistore_cookie_necessary_title" />{' '}
                        </strong>
                        <small>
                          <T name="dosistore_cookie_necessary_desc" />
                        </small>
                      </Flex>
                    </Flex>
                  )}
                </Field>
              </li>
              <li>
                <Field name="ana" type="checkbox">
                  {({ field }: FieldProps) => (
                    <Flex container as="label">
                      <Checkbox className={styles.checkbox} {...field} variant="circle" />
                      <Flex className={styles.labelText} flex={1}>
                        <strong>
                          <span>
                            <T name="dosistore_cookie_analytics_title" />{' '}
                          </span>
                        </strong>
                        <small>
                          <T name="dosistore_cookie_analytics_desc" />
                        </small>
                      </Flex>
                    </Flex>
                  )}
                </Field>
              </li>
              <li>
                <Field name="mkt" type="checkbox">
                  {({ field }: FieldProps) => (
                    <Flex container as="label">
                      <Checkbox className={styles.checkbox} {...field} variant="circle" />
                      <Flex className={styles.labelText} flex={1}>
                        <strong>
                          <span>
                            <T name="dosistore_cookie_marketing_title" />{' '}
                          </span>
                        </strong>
                        <small>
                          <T name="dosistore_cookie_marketing_desc" />
                        </small>
                      </Flex>
                    </Flex>
                  )}
                </Field>
              </li>
            </List>
          )}
        </div>
      </div>
      <div className={styles.footer}>
        <Flex container className={styles.buttons}>
          <Flex flex={1}>
            <Button
              className={classnames(styles.button, styles.requiredButton)}
              type="submit"
              block
              color="default"
              size="large"
              disabled={disabled}>
              <T
                name={
                  isSettingsVisible
                    ? 'citizen_cookie_acceptselections'
                    : 'citizen_cookie_acceptrequired'
                }
              />
            </Button>
          </Flex>
          <Flex flex={1}>
            <Button
              className={classnames(styles.button, styles.allButton)}
              block
              color="primary"
              size="large"
              onClick={onAcceptAllButtonClick}>
              <T name="citizen_cookie_acceptall" />
            </Button>
          </Flex>
        </Flex>
      </div>
    </Form>
  );
};
